import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Cpu } from "lucide-react";
// El tipo del catalogo lo define la capa de API, que es quien lo recibe. Una
// copia local compilaria igual por tipado estructural, pero el dia que
// `/assistants` añada un campo el componente no se enteraria y nadie fallaria.
import type { ModeloCatalogo } from "@/lib/api/chatApi";

/**
 * Selector de modelo POR CONVERSACION, solo para admins.
 *
 * Recibe TODO por props y no toca el runtime de assistant-ui a proposito: asi
 * se puede probar sin montar el thread. Montar `ThreadPrimitive.Messages` sobre
 * `useLocalRuntime` en jsdom lanza "Maximum update depth exceeded" incluso con
 * las versiones buenas del paquete (ver CLAUDE.md), asi que un componente que
 * solo funcionara dentro del runtime seria un componente sin red.
 */

export type { ModeloCatalogo };

interface Props {
  /** Del JWT ya validado (`user?.role === "admin"`), nunca del body. */
  isAdmin: boolean;
  /** Catalogo habilitado; `/assistants` solo se lo manda a los admins. */
  modelos: ModeloCatalogo[];
  /** Modelo con el que el agente corre hoy si nadie elige nada. */
  modeloDefault: string;
  /** `model_override` del thread, o `null` si corre con el default. */
  modeloElegido: string | null;
  /** `null` = volver al default (el backend lo entiende como "sin override"). */
  onElegir: (modelId: string | null) => void;
}

function etiqueta(modelos: ModeloCatalogo[], modelId: string): string {
  const m = modelos.find((x) => x.model_id === modelId);
  // Sin entrada en el catalogo se muestra el id CRUDO en vez de caer al
  // default: un modelo se puede deshabilitar despues de que una conversacion lo
  // fijara, y decir "por defecto" ahi mentiria sobre con que va a correr.
  return m?.label?.trim() || modelId;
}

export default function SelectorModelo({
  isAdmin,
  modelos,
  modeloDefault,
  modeloElegido,
  onElegir,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // El catalogo revela que modelos se pagan y elegir modelo es una herramienta
  // de administracion: para el resto no se pinta NADA. El backend descarta
  // ademas el override de un no admin (`_modelo_pedido`), asi que esto es la
  // capa de interfaz de una regla que ya se impone en el servidor.
  if (!isAdmin) return null;
  // Fail-safe de `/assistants`: si no pudo leer el catalogo llega vacio. Un
  // desplegable sin opciones invita a hacer clic para nada.
  if (modelos.length === 0) return null;

  const efectivo = modeloElegido ?? modeloDefault;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title={t("chat.modelo.tooltip")}
        aria-label={t("chat.modelo.aria")}
        className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
          open
            ? "border-[var(--primary)] text-[var(--foreground)] ring-1 ring-[var(--primary)]"
            : "border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--muted-foreground)] hover:text-[var(--foreground)]"
        }`}
      >
        <Cpu size={14} />
        <span>{etiqueta(modelos, efectivo)}</span>
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute bottom-full right-0 mb-2 min-w-[240px] rounded-xl border border-[var(--border)] bg-[var(--background)] p-1.5 shadow-xl"
        >
          {modelos.map((m) => {
            const esDefault = m.model_id === modeloDefault;
            const activo = m.model_id === efectivo;
            // Una vez la conversacion tiene override guardado no hay vuelta
            // atras: el `COALESCE` del backend ignora el NULL con el que se
            // pediria. Se deshabilita en vez de ofrecerlo y no cumplirlo.
            const bloqueado = esDefault && modeloElegido !== null;
            return (
              <button
                key={m.model_id}
                type="button"
                role="option"
                aria-selected={activo}
                disabled={bloqueado}
                title={bloqueado ? t("chat.modelo.sinVuelta") : undefined}
                onClick={() => {
                  if (bloqueado) return;
                  // Elegir el default manda `null`, NO su id: guardarlo como
                  // override dejaria la conversacion clavada en ese modelo el
                  // dia que un admin cambie el default del agente.
                  onElegir(esDefault ? null : m.model_id);
                  setOpen(false);
                }}
                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors ${
                  bloqueado ? "cursor-not-allowed opacity-50" : "hover:bg-[var(--muted)]"
                } ${activo ? "bg-[var(--muted)] font-semibold" : ""}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium text-[var(--foreground)]">
                    {etiqueta(modelos, m.model_id)}
                  </div>
                  {esDefault && (
                    <div className="text-[11px] text-[var(--muted-foreground)]">
                      {t("chat.modelo.porDefecto")}
                    </div>
                  )}
                </div>
                {activo && (
                  <svg
                    className="h-4 w-4 shrink-0 text-[var(--primary)]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface BadgeProps {
  /** Modelo que genero ESTE mensaje, o `null` si no consta. */
  modelo: string | null;
  /** El thread tiene `model_override` fijado por un admin. */
  threadTieneOverride: boolean;
}

/**
 * Badge "este mensaje corrio con el modelo X".
 *
 * **No dice "difiere del default", y no es un descuido.** El default de un
 * agente es MUTABLE: un admin lo cambia desde el panel. Comparar un mensaje de
 * hace dos dias contra el default de hoy hace que el badge aparezca o
 * desaparezca segun lo que alguien toque despues, sin que nada cambie en la
 * conversacion — afirmaria algo falso sobre el pasado, justo sobre la pregunta
 * que el badge existe para responder ("¿esto fue una prueba?").
 *
 * Por eso se ancla a `model_override` DEL THREAD, que es un dato de la propia
 * conversacion y no del catalogo de hoy. Es la misma distincion que ya hace el
 * backend en `threads.py`, donde `model_override` no se disfraza devolviendo el
 * modelo efectivo "para que el front distinga 'elegido' de 'heredado'".
 */
export function BadgeModelo({ modelo, threadTieneOverride }: BadgeProps) {
  const { t } = useTranslation();
  if (!threadTieneOverride || !modelo) return null;
  return (
    <span
      title={t("chat.modelo.badgeTooltip")}
      className="inline-flex items-center gap-1 rounded-full border border-[var(--border)] px-2 py-0.5 text-[11px] font-medium text-[var(--muted-foreground)]"
    >
      <Cpu size={11} />
      {t("chat.modelo.badge", { modelo })}
    </span>
  );
}
